import { useState } from 'react';
import type { FormEvent } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Navigate, useSearchParams } from 'react-router';
import type { ApiError } from '../api';
import { googleStartUrl, requestSignInLink, signInOptions } from '../api';
import { useSession } from '../session';
import { PLATFORM_NAME } from '@bre-ticketing/contracts';
import { ErrorNote, FullPageMessage } from '../ui';

/**
 * Staff sign-in (D-370): "Continue with Google" when it is set up, and an
 * emailed single-use link always. Google is a top-level navigation, not a
 * fetch, because the flow leaves for Google and comes back with a code. The
 * link lands on `/api/auth/email/verify` on this host, which sets the cookie
 * and returns here.
 */

/** What the API's `?error=` codes mean to the person reading them. */
const ERRORS: Record<string, string> = {
  expired: 'That sign-in link has expired or was already used. Ask for a new one below.',
  invalid: 'That sign-in did not work. Try again, or ask for an emailed link.',
  pending: 'You proved your address, but it has no staff access here yet. An administrator has been shown your request under Staff → Pending logins.',
  inactive: 'This staff account is suspended or deactivated. Ask an administrator.',
  no_access: 'This staff account may not use Admin. Ask an administrator for access.',
  unverified: 'Google has not verified that address. Use an emailed link instead.',
};

export default function LoginPage() {
  const session = useSession();
  const [params] = useSearchParams();
  const [email, setEmail] = useState('');
  const options = useQuery({ queryKey: ['sign-in-options'], queryFn: signInOptions, staleTime: 60_000 });

  const returnTo = params.get('return_to') ?? '/';
  const send = useMutation<unknown, ApiError, string>({ mutationFn: (address) => requestSignInLink(address, returnTo) });

  if (session.isPending) return <FullPageMessage title="Checking your session…" />;
  if (session.data) return <Navigate to="/" replace />;

  const error = params.get('error');
  const google = options.data?.google === true;

  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (email.trim()) send.mutate(email.trim());
  };

  return (
    <div className="grid min-h-full place-items-center bg-shell-900 p-6">
      <div className="w-full max-w-sm rounded-lg bg-surface p-8">
        <h1 className="text-lg font-semibold text-ink-900">{PLATFORM_NAME}</h1>
        <p className="mt-1 text-sm text-ink-400">Staff administration</p>

        {error ? (
          <div className="mt-5">
            <ErrorNote>{ERRORS[error] ?? 'Sign-in did not work. Try again.'}</ErrorNote>
          </div>
        ) : null}

        {google ? (
          <>
            <a
              href={googleStartUrl(returnTo)}
              className="mt-6 flex w-full items-center justify-center rounded bg-accent px-3 py-2.5 text-sm font-medium text-white transition-colors hover:bg-accent/90"
            >
              Continue with Google
            </a>
            <p className="my-4 text-center text-xs text-ink-400">or</p>
          </>
        ) : (
          <div className="mt-6" />
        )}

        {send.isSuccess ? (
          <div className="rounded border border-line bg-page px-3 py-3 text-sm text-ink-900" role="status">
            <p className="font-medium">Check your email</p>
            <p className="mt-1 text-ink-600">
              If <strong>{email.trim()}</strong> can sign in here, a link is on its way. It works once and expires in 15 minutes.
            </p>
            <button type="button" className="mt-2 text-xs text-accent underline underline-offset-2" onClick={() => send.reset()}>
              Use a different address
            </button>
          </div>
        ) : (
          <form onSubmit={submit}>
            <label htmlFor="login-email" className="block text-xs font-semibold tracking-wide text-ink-400 uppercase">
              Email me a sign-in link
            </label>
            <input
              id="login-email"
              type="email"
              autoComplete="email"
              required
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              placeholder="you@example.com"
              className="mt-2 w-full rounded border border-line bg-surface px-3 py-2 text-sm"
            />
            {send.error ? (
              <div className="mt-2">
                <ErrorNote>{send.error.message}</ErrorNote>
              </div>
            ) : null}
            <button
              type="submit"
              disabled={send.isPending || !email.trim()}
              className={`mt-3 flex w-full items-center justify-center rounded px-3 py-2.5 text-sm font-medium transition-colors disabled:opacity-50 ${google ? 'border border-line text-ink-900 hover:bg-page' : 'bg-accent text-white hover:bg-accent/90'}`}
            >
              {send.isPending ? 'Sending…' : 'Email me a link'}
            </button>
          </form>
        )}

        <p className="mt-4 text-xs text-ink-400">
          Window and gate staff sign in with a PIN on a paired device, not here.
        </p>
      </div>
    </div>
  );
}
